/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/auth/CustomSignIn.tsx
 * Release: v2.0.0 - Login identico alla release precedente (FUNZIONANTE)
 * Data: Dicembre 2024
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthProvider';
import coronatekLogo from './asset/Coronatek_Logo_bordato_92dpi_200x88_1.webp';
import coronatekIntro from './asset/Intro_Coronatek.mp4';
import bgGradient from './asset/bg-gradient.jpg';
import dialogicomLogo from './asset/Dialogicom_432x144.webp';
import GoogleSignInButton from './GoogleSignInButton';
import { destinazioneDopoLogin } from './postLogin';

const CustomSignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [skipVideo, setSkipVideo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthOnly, setOauthOnly] = useState<null | { provider: string }>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const { login, refreshUser } = useAuth();

  // Controlla se saltare il video
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('skipVideo') === 'true') {
      setSkipVideo(true);
      setShowForm(true);
    }
  }, []);

  // Play reduced video on first render without hover
  useEffect(() => {
    if (showForm) {
      videoRef.current?.play();
    }
  }, [showForm]);

  const handleVideoEnd = () => setShowForm(true);
  const handleVideoHover = () => {
    videoRef.current?.play();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        console.log('✅ Accesso effettuato con successo!');
        // Refresh user data per ottenere status aggiornato
        try { 
          const updatedUserData = await refreshUser(); 
          
          // Check diretto sui dati freschi dal backend (più affidabile del context)
          if ((updatedUserData as any)?.needs_profile_completion || (updatedUserData as any)?.status === 'pending_completion') {
            console.log('🔄 REDIRECT DIRETTO: Utente deve completare profilo');
            
            // Redirect intelligente basato sui permessi utente
            const user = updatedUserData as any;
            if (user.role === 'admin' || user.role === 'superadmin' || user.group_id === 'accettazione') {
              // Admin → Form completo full-screen
              window.location.replace(`/admin/profile-form?userId=${user.id}&email=${encodeURIComponent(user.email)}&from=login`);
            } else {
              // Utenti normali → Form semplice
              window.location.replace('/complete-profile?from=login');
            }
            return;
          }
        } catch (error) {
          console.warn('Warning: Impossibile verificare stato profilo:', error);
        }
        
        // Redirect normale per utenti con profilo completo.
        // Se si arrivava qui da una pagina protetta, ci si torna (?next=), altrimenti dashboard.
        const dove = destinazioneDopoLogin();
        console.log('✅ Utente completo - redirect', dove);
        window.location.replace(dove);
        return;
      } else {
        console.error('❌ Errore login:', result.error);
        setError(result.error || 'Credenziali non valide');
        // Se il backend segnala oauth_only, mostro modale
        try {
          const parsed = result as any;
          if (parsed.oauth_only && parsed.provider === 'google') {
            setOauthOnly({ provider: 'google' });
          }
        } catch {}
      }
    } catch (error) {
      console.error('Errore login:', error);
      setError('Errore di connessione. Riprova più tardi.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fullscreen intro video if not skipping
  if (!showForm) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black">
        <video
          ref={videoRef}
          src={coronatekIntro}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnd}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-cover text-white overflow-auto"
      style={{ backgroundImage: `url(${bgGradient})` }}
    >
      {oauthOnly && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full text-center">
            <h2 className="text-xl font-semibold mb-2">Account registrato con Google</h2>
            <p className="text-gray-300 mb-4">Per accedere, usa il pulsante "Continua con Google".</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setOauthOnly(null)} className="flex-1 py-2 rounded bg-slate-700 hover:bg-slate-600">Chiudi</button>
              <a href="#google-btn" className="flex-1 py-2 rounded bg-green-600 hover:bg-green-500">Vai a Google</a>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-6 py-3 bg-slate-800 z-10 h-32">
        <img src={coronatekLogo} alt="Coronatek Logo" className="h-10 sm:h-16 drop-shadow-xl" />
        <div className="text-right">
          <h1 className="text-3xl sm:text-5xl font-bold text-green-400 mb-1 px-2">Turbomanager</h1>
          <p className="text-base sm:text-lg text-gray-400 px-2">Dalla presa in carico alla riconsegna</p>
        </div>
      </div>

      <div className="min-h-screen flex flex-col md:flex-row items-start gap-8 px-4 pt-40 pb-4 max-w-7xl mx-auto">
        {/* Left: Form */}
        <div className="w-full md:w-1/2 animate-slide-in-left transition-all duration-1000 ease-out">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 space-y-4">
            <div id="google-btn">
              <GoogleSignInButton className="w-full" />
            </div>
            <div className="text-center text-gray-400">oppure</div>
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm text-center">
                  {error}
                </div>
              )}
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                className="w-full px-3 py-2 border rounded bg-slate-900 text-white border-slate-700"
                required
                disabled={isLoading}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                className="w-full px-3 py-2 border rounded bg-slate-900 text-white border-slate-700"
                required
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-green-600 hover:bg-green-500 text-white py-2 rounded transform transition-transform duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Accesso in corso...' : 'Accedi'}
              </button>
            </form>
            <div className="flex justify-between text-xs text-gray-400">
              <a href="/forgot-password?skipVideo=true" className="hover:text-white">Password dimenticata?</a>
              <a href="/register?skipVideo=true" className="hover:text-white">Registrati</a>
            </div>
            <div className="pt-2 text-center">
              <a
                href="https://dialogicom.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform duration-200 hover:scale-105"
              >
                <div className="text-xs text-gray-400 mb-1">Powered by</div>
                <img
                  src={dialogicomLogo}
                  alt="Dialogicom Logo"
                  className="mx-auto w-[200px]"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Right: Video + Text */}
        <div className="w-full md:w-1/2 flex flex-col justify-between animate-slide-in-up transition-all duration-1000 ease-out">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 w-full aspect-video overflow-hidden border border-slate-600">
            <video
              ref={videoRef}
              src={coronatekIntro}
              muted
              preload={skipVideo ? 'auto' : 'metadata'}
              autoPlay={skipVideo}
              onMouseOver={handleVideoHover}
              className="w-full h-full rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 w-full mt-2 animate-slide-in-up transition-all duration-1000 ease-out delay-500">
            <p className="text-gray-300 text-sm leading-relaxed mb-0">
              Con <span className="font-semibold text-green-400">TurboManager</span>, Coronatek porta l&apos;officina nell&apos;era dell&apos;<strong>Intelligenza Artificiale</strong>: un gestionale smart, intuitivo e basato sulle tecnologie più recenti, creato per semplificare il lavoro e aumentare l&apos;efficienza.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed">
              Tutto questo per offrire ai clienti un servizio sempre più <strong>preciso, rapido e professionale</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSignIn;